import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {blue} from '@mui/material/colors';
import {FC, useEffect, useMemo, useState} from 'react';
import {useRecoilStateLoadable, useRecoilValue} from 'recoil';
import {useStore} from 'zustand';
import {filterLabelMap, filterServiceRequestsBy, statusCodes} from '../../../../../Filters/constants/filters.data';
import {FiltersAccordion} from '../../../../../Filters/ui/FiltersAccordion';
import {taskLocationsMapWithNamesState} from '../../../../../../store/taskLocations/atoms';
import {taskLocationsDictQuery} from '../../../../../../store/taskLocations/selectors';
import {ITaskLocationsDict} from '../../../../../../shared/types/taskLocationsDict.types';
import {useSearchStrategy} from '../../../../../../hooks/useSearchStrategy';
import {useDaData} from '../../../../../../hooks/useDaData';
import {
  IWorkerSelectOption,
  searchWorkersForFilterStrategy,
} from '../../../../../../api/strategies/searchWorkersStrategy';
import {UserAccountService} from '../../../../../../api/services/user-account.service';
import {getFullName} from '../../../../../../shared/helpers/getFullName';
import {useStoresContext} from '../../hooks/useStoresContext';
import {
  DASHBOARD_SERVICE_REQUEST_FILTER_KEYS,
  DashboardSearchBy,
} from '../../store/useDashboardServiceRequestsFiltersStore';

interface Props {
  serviceType: string;
}

type FilterOption = [string, string];
type SelectOption = {label: string; value: string};

const dashboardFilterOptions: FilterOption[] = [
  ...(filterServiceRequestsBy as FilterOption[]),
  ['Адрес', 'fullAddress'],
];
const dashboardFilterLabelMap = new Map<string, string>([
  ...Array.from(filterLabelMap.entries()),
  ['fullAddress', 'Адрес'],
]);

const actionButtonStyle = {
  fontSize: '14px',
  borderRadius: 50,
  height: '32px',
  minHeight: '32px',
  backgroundColor: blue[50],
  '&:hover': {
    backgroundColor: blue[100],
  },
  color: '#000',
  border: `1px solid ${blue[100]}`,
  textTransform: 'none',
} as const;

const controlOffsetTop = '10px';
const accordionWidth = 300;
const accordionOverlaySx = {
  position: 'relative',
  width: `${accordionWidth}px`,
  minWidth: `${accordionWidth}px`,
  height: '54px',
  flexShrink: 0,
  '& .MuiAccordion-root': {
    position: 'absolute',
    top: controlOffsetTop,
    left: 0,
    overflow: 'visible',
    width: `${accordionWidth}px`,
    margin: '0 !important',
    backgroundColor: '#F6F7FB',
    borderRadius: '4px',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '40px',
    borderRadius: '4px',
  },
  '& .MuiAccordionSummary-content': {
    my: '0 !important',
  },
  '& .MuiAccordionDetails-root': {
    backgroundColor: '#fff',
    border: '1px solid #E2E6EF',
    borderRadius: '0 0 4px 4px',
    boxShadow: '0 8px 24px rgba(70, 66, 85, 0.12)',
  },
} as const;

const searchOptions: {value: DashboardSearchBy; label: string; disabled?: boolean}[] = [
  {value: 'id', label: 'Номер заявки'},
  {value: 'extNumber', label: 'Лицевой счет'},
  {value: 'deviceNumberNew', label: 'Номер нового счетчика'},
];

export const DashboardServiceRequestsFilters: FC<Props> = ({serviceType}) => {
  const {dashboardServiceRequestsFiltersStore} = useStoresContext(serviceType);
  const {
    searchBy,
    searchValue,
    filters,
    filterSets,
    currentFilterSetId,
    setSearchBy,
    setSearchValue,
    addFilter,
    removeFilter,
    resetAll,
    saveCurrentFilterSet,
    applyFilterSet,
    deleteFilterSet,
  } = useStore(dashboardServiceRequestsFiltersStore);

  const [{contents: taskLocationsContents}] = useRecoilStateLoadable(taskLocationsDictQuery);
  const taskLocationLabelByCode = useRecoilValue(taskLocationsMapWithNamesState);

  const [selectedFilterBy, setSelectedFilterBy] = useState<string>('');
  const [selectedFilterValue, setSelectedFilterValue] = useState('');
  const [selectedWorker, setSelectedWorker] = useState<IWorkerSelectOption<string> | null>(null);
  const [workerLabelByUid, setWorkerLabelByUid] = useState<Record<string, string>>({});
  const [saveSetName, setSaveSetName] = useState('');
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const {handleSearch, data: workers, searchLoading, searchTerm, resetSearch} = useSearchStrategy<
    IWorkerSelectOption<string>
  >(selectedFilterBy === 'workerUid' ? searchWorkersForFilterStrategy : null);
  const {handleChangeAddress, suggestions: addressSuggestions} = useDaData();

  const availableFilters = useMemo<FilterOption[]>(
    () =>
      dashboardFilterOptions.filter(
        (item: string[]) =>
          DASHBOARD_SERVICE_REQUEST_FILTER_KEYS.has(item[1]) &&
          !filters.find((selectedFilter) => selectedFilter[0] === item[1]),
      ) as FilterOption[],
    [filters],
  );

  const addressOptions = useMemo(
    () => addressSuggestions.map((suggestion) => suggestion.unrestricted_value),
    [addressSuggestions],
  );

  const taskLocations = useMemo<SelectOption[]>(
    () =>
      Array.isArray(taskLocationsContents?.content)
        ? taskLocationsContents.content.map((item: ITaskLocationsDict) => ({
            label: item.name,
            value: item.code,
          }))
        : [],
    [taskLocationsContents],
  );

  const predefinedOptions = useMemo<Record<string, SelectOption[]>>(
    () => ({
      status: statusCodes.map(([label, value]: [string, string]) => ({label, value})),
      taskLocationCode: taskLocations,
    }),
    [taskLocations],
  );

  const handleAddFilter = () => {
    if (!selectedFilterBy || !selectedFilterValue.trim()) {
      return;
    }

    addFilter([selectedFilterBy, selectedFilterValue.trim()]);
    setSelectedFilterBy('');
    setSelectedFilterValue('');
    setSelectedWorker(null);
    resetSearch();
  };

  const handleSaveSet = () => {
    if (!saveSetName.trim()) {
      return;
    }

    saveCurrentFilterSet(saveSetName);
    setSaveSetName('');
    setIsSaveDialogOpen(false);
  };

  const getDisplayedFilterValue = (filterBy: string, value: string) => {
    if (filterBy === 'taskLocationCode') {
      return taskLocationLabelByCode.get(value) ?? value;
    }

    if (filterBy === 'workerUid') {
      return workerLabelByUid[value] ?? value;
    }

    return predefinedOptions[filterBy]?.find((item) => item.value === value)?.label ?? value;
  };

  useEffect(() => {
    const missingWorkerUids = filters
      .filter(([filterBy, value]) => filterBy === 'workerUid' && value && !workerLabelByUid[value])
      .map(([, value]) => value);

    if (!missingWorkerUids.length) {
      return;
    }

    let isMounted = true;

    Promise.allSettled(
      missingWorkerUids.map(async (workerUid) => {
        const {data} = await UserAccountService.getById(workerUid);

        return [workerUid, getFullName(data)] as const;
      }),
    ).then((results) => {
      if (!isMounted) {
        return;
      }

      const loadedLabels = results.reduce<Record<string, string>>((acc, result) => {
        if (result.status === 'fulfilled') {
          const [workerUid, label] = result.value;

          acc[workerUid] = label;
        }

        return acc;
      }, {});

      if (Object.keys(loadedLabels).length) {
        setWorkerLabelByUid((prev) => ({
          ...prev,
          ...loadedLabels,
        }));
      }
    });

    return () => {
      isMounted = false;
    };
  }, [filters, workerLabelByUid]);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          mt: 0.5,
          mb: 1.5,
          p: 2,
          backgroundColor: '#F6F7FB',
          border: '1px solid #E2E6EF',
          borderRadius: '12px',
          overflow: 'visible',
        }}
      >
        <Stack direction="column" spacing={2}>
          <Box sx={{position: 'relative', minHeight: '54px', zIndex: 5, overflow: 'visible'}}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{
                width: '100%',
                overflow: 'visible',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{flexShrink: 0}}>
                <Box sx={{width: 350, pt: controlOffsetTop, flexShrink: 0}}>
                  <TextField
                    size="small"
                    placeholder="Значение поиска"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <>
                          {searchValue ? (
                            <Tooltip title="Очистить">
                              <IconButton size="small" onClick={() => setSearchValue('')}>
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <SearchIcon fontSize="small" />
                        </>
                      ),
                    }}
                  />
                </Box>

                <FormControl variant="standard" sx={{minWidth: 220, flexShrink: 0, backgroundColor: '#F6F7FB'}}>
                  <InputLabel id="dashboard-search-by-label">Критерий поиска</InputLabel>
                  <Select
                    labelId="dashboard-search-by-label"
                    id="dashboard-search-by"
                    value={searchBy}
                    onChange={(e) => setSearchBy(e.target.value as DashboardSearchBy)}
                  >
                    {searchOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{flexShrink: 0, ml: 2}}>
                <Box sx={accordionOverlaySx}>
                  <FiltersAccordion
                    summaryIcon={<AddIcon sx={{mr: 1}} fontSize="small" />}
                    summaryContent="Новый фильтр"
                  >
                    <Stack spacing={1}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="dashboard-filter-by-label">Тип фильтра</InputLabel>
                        <Select
                          labelId="dashboard-filter-by-label"
                          value={selectedFilterBy}
                          label="Тип фильтра"
                          onChange={(e) => {
                            setSelectedFilterBy(e.target.value);
                            setSelectedFilterValue('');
                            setSelectedWorker(null);
                            resetSearch();
                          }}
                        >
                          {availableFilters.map(([label, value]: FilterOption) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {selectedFilterBy ? (
                        predefinedOptions[selectedFilterBy] ? (
                          <FormControl size="small" fullWidth>
                            <InputLabel id="dashboard-filter-value-label">Значение</InputLabel>
                            <Select
                              labelId="dashboard-filter-value-label"
                              value={selectedFilterValue}
                              label="Значение"
                              onChange={(e) => setSelectedFilterValue(e.target.value)}
                            >
                              {predefinedOptions[selectedFilterBy].map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : selectedFilterBy === 'workerUid' ? (
                          <Autocomplete
                            size="small"
                            value={selectedWorker}
                            inputValue={searchTerm}
                            options={workers}
                            loading={searchLoading}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            onChange={(_, option) => {
                              setSelectedWorker(option);
                              setSelectedFilterValue(option?.value ?? '');

                              if (option) {
                                setWorkerLabelByUid((prev) => ({
                                  ...prev,
                                  [option.value]: option.label,
                                }));
                              }
                            }}
                            onInputChange={(_, value) => handleSearch(value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Значение"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        ) : selectedFilterBy === 'fullAddress' ? (
                          <Autocomplete
                            size="small"
                            freeSolo
                            value={selectedFilterValue}
                            inputValue={selectedFilterValue}
                            options={addressOptions}
                            filterOptions={(options) => options}
                            onChange={(_, value) => setSelectedFilterValue(value ?? '')}
                            onInputChange={(_, value) => {
                              setSelectedFilterValue(value);
                              handleChangeAddress(value);
                            }}
                            renderInput={(params) => <TextField {...params} label="Значение" />}
                          />
                        ) : (
                          <TextField
                            size="small"
                            label="Значение"
                            value={selectedFilterValue}
                            onChange={(e) => setSelectedFilterValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAddFilter();
                              }
                            }}
                            fullWidth
                          />
                        )
                      ) : null}

                      <Stack sx={{justifyContent: 'flex-end', mt: 1}} direction="row" spacing={2}>
                        <Button
                          sx={{fontWeight: 'bold'}}
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedFilterBy('');
                            setSelectedFilterValue('');
                          }}
                        >
                          Отмена
                        </Button>
                        <Button
                          sx={{fontWeight: 'bold'}}
                          size="small"
                          disabled={!selectedFilterBy || !selectedFilterValue.trim()}
                          onClick={handleAddFilter}
                        >
                          Применить
                        </Button>
                      </Stack>
                    </Stack>
                  </FiltersAccordion>
                </Box>

                <Box sx={accordionOverlaySx}>
                  <FiltersAccordion
                    summaryContent={
                      currentFilterSetId
                        ? filterSets.find((item) => item.id === currentFilterSetId)?.name ?? 'Наборы фильтров'
                        : 'Наборы фильтров'
                    }
                    disabled={!filterSets.length}
                  >
                    <Stack spacing={0.5}>
                      {filterSets.map((item) => (
                        <Stack
                          key={item.id}
                          direction="row"
                          sx={{
                            backgroundColor: currentFilterSetId === item.id ? blue[50] : 'inherit',
                            justifyContent: 'space-between',
                            '&:hover': {
                              backgroundColor: '#DDDDDD',
                            },
                            p: 0.25,
                          }}
                        >
                          <Box
                            onClick={() => applyFilterSet(item.id)}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              width: '180px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              pl: 1,
                            }}
                          >
                            <Typography noWrap>{item.name}</Typography>
                          </Box>
                          <IconButton size="small" onClick={() => deleteFilterSet(item.id)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  </FiltersAccordion>
                </Box>
              </Stack>
            </Stack>

          </Box>

          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'stretch',
              gap: 2,
              minHeight: '32px',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                minWidth: 0,
                flex: 1,
                minHeight: '32px',
                alignItems: 'flex-start',
                overflowX: 'auto',
                overflowY: 'hidden',
                '& > *': {
                  flexShrink: 0,
                },
              }}
            >
              {filters.map(([filterBy, value]) => (
                <Chip
                  key={`${filterBy}:${value}`}
                  variant="outlined"
                  label={`${dashboardFilterLabelMap.get(filterBy)}: ${getDisplayedFilterValue(filterBy, value)}`}
                  onDelete={() => removeFilter(filterBy)}
                  sx={{
                    height: '32px',
                    backgroundColor: '#FFECCC',
                    border: '1px solid #FF9F00',
                    '& .MuiChip-label': {
                      px: 1.5,
                    },
                  }}
                />
              ))}
            </Stack>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{flexShrink: 0, minHeight: '32px', alignItems: 'flex-start'}}
            >
              <Button
                sx={actionButtonStyle}
                size="small"
                variant="contained"
                endIcon={<SaveIcon color={!filters.length && !searchValue.trim() ? 'disabled' : 'primary'} />}
                disabled={!filters.length && !searchValue.trim()}
                onClick={() => setIsSaveDialogOpen(true)}
              >
                Сохранить набор
              </Button>
              <Button
                sx={actionButtonStyle}
                size="small"
                variant="contained"
                disabled={!filters.length && !searchValue.trim()}
                onClick={resetAll}
              >
                Сбросить все
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <Dialog open={isSaveDialogOpen} onClose={() => setIsSaveDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Сохранить набор фильтров</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название набора"
            fullWidth
            value={saveSetName}
            onChange={(e) => setSaveSetName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSaveDialogOpen(false)} color="inherit">
            Отмена
          </Button>
          <Button onClick={handleSaveSet} disabled={!saveSetName.trim()}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
